import { useCallback, useEffect, useRef, useState } from "react";
import { scopedIdbGet as get, scopedIdbSet as set } from "@/lib/client-scoped-idb";

/**
 * Persisted board state for the Handshake Board. Modeled after the
 * vanilla `hbDrawCanvas` + `hbItems` flow in `_legacy/wd.html` lines
 * 991-999 — except vanilla committed pixels straight to the 2D context
 * and stickies straight to the DOM, with no persistence. Our port
 * keeps each stroke and each sticky as data so:
 *
 *   - undo/clear can re-render the canvas deterministically from the
 *     stroke list (a simple `pop` is enough);
 *   - facilitators can reload the page mid-workshop without losing
 *     their work (idb-keyval, keyed by workshop id);
 *   - undo is unified: a single `actionLog` records the order in which
 *     strokes and stickies were added so `undo()` reverses the most
 *     recent action regardless of kind, matching the vanilla feel
 *     where the last thing on the board comes off first.
 *
 * IndexedDB writes are best-effort: we persist after every mutation,
 * but ignore rejections (the canvas remains usable in-memory even if
 * the browser blocks storage in private mode).
 *
 * Storage payload is versioned-by-shape: legacy installs may have
 * written a bare `Stroke[]`; on load we detect that and migrate to
 * the current `{ strokes, stickies, actionLog }` envelope.
 */

export type StrokePoint = { x: number; y: number };

export type Stroke = {
  id: string;
  color: string;
  points: StrokePoint[];
  width: number;
};

export type Sticky = {
  id: string;
  text: string;
  /** Resolved CSS colour (rgb / hex / var(...)) used for the sticky's text + border. */
  color: string;
  /** "r,g,b" triplet (or a `var(--…-rgb)` reference) for tinted backgrounds. */
  rgb: string;
  /** Pixels from the board wrapper's left edge. */
  x: number;
  /** Pixels from the board wrapper's top edge. */
  y: number;
  /** Optional explicit width override; defaults applied at render time. */
  width?: number;
};

export type BoardAction = {
  kind: "stroke" | "sticky";
  id: string;
};

export type PersistedBoard = {
  strokes: Stroke[];
  stickies: Sticky[];
  actionLog: BoardAction[];
};

function storageKey(workshopId: string): string {
  return `xaes:handshake:strokes:${workshopId}`;
}

type UseHandshakeStrokesReturn = {
  strokes: Stroke[];
  stickies: Sticky[];
  /** Live action log — exposed so callers can serialise the full
   *  board envelope for the snapshot system. */
  actionLog: BoardAction[];
  addStroke: (stroke: Stroke) => void;
  addSticky: (sticky: Sticky) => void;
  updateSticky: (
    id: string,
    patch: Partial<Pick<Sticky, "x" | "y" | "text">>,
  ) => void;
  /** Remove a sticky outright (also drops its action-log entry). Used
   *  when a freshly-added free-form note is committed empty. */
  removeSticky: (id: string) => void;
  clear: () => void;
  undo: () => void;
  /**
   * Wholesale replace the board with a persisted payload. Used by the
   * snapshot restore flow — see `src/lib/use-snapshot.ts`. Persists
   * the new state to idb-keyval in the same write pass.
   */
  replaceAll: (next: PersistedBoard) => void;
  hydrated: boolean;
};

/**
 * Load persisted board state from idb-keyval on mount, then persist
 * on every change. `hydrated` flips to true once the initial load
 * resolves so callers can avoid clobbering the persisted data with
 * the empty default during the first render pass.
 */
export function useHandshakeStrokes(
  workshopId: string,
): UseHandshakeStrokesReturn {
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [stickies, setStickies] = useState<Sticky[]>([]);
  const [actionLog, setActionLog] = useState<BoardAction[]>([]);
  const [hydrated, setHydrated] = useState(false);
  // `disposed` guards against the StrictMode double-invoke pattern —
  // if the effect tears down before the async load resolves, we must
  // not call setState on the now-detached component.
  const disposedRef = useRef(false);

  useEffect(() => {
    disposedRef.current = false;
    let cancelled = false;

    void get<PersistedBoard | Stroke[] | undefined>(storageKey(workshopId))
      .then((stored) => {
        if (cancelled || disposedRef.current) return;
        if (Array.isArray(stored)) {
          // Legacy shape: bare `Stroke[]`. Migrate forward.
          setStrokes(stored);
          setStickies([]);
          setActionLog(stored.map((s) => ({ kind: "stroke", id: s.id })));
        } else if (stored && typeof stored === "object") {
          setStrokes(Array.isArray(stored.strokes) ? stored.strokes : []);
          setStickies(Array.isArray(stored.stickies) ? stored.stickies : []);
          if (Array.isArray(stored.actionLog) && stored.actionLog.length > 0) {
            setActionLog(stored.actionLog);
          } else {
            // No log persisted (older shape): rebuild a best-effort
            // log by listing strokes first, then stickies. Order
            // within each kind matches insertion order.
            const rebuilt: BoardAction[] = [
              ...(Array.isArray(stored.strokes)
                ? stored.strokes.map<BoardAction>((s) => ({
                    kind: "stroke",
                    id: s.id,
                  }))
                : []),
              ...(Array.isArray(stored.stickies)
                ? stored.stickies.map<BoardAction>((s) => ({
                    kind: "sticky",
                    id: s.id,
                  }))
                : []),
            ];
            setActionLog(rebuilt);
          }
        } else {
          setStrokes([]);
          setStickies([]);
          setActionLog([]);
        }
        setHydrated(true);
      })
      .catch(() => {
        if (cancelled || disposedRef.current) return;
        setHydrated(true);
      });

    return () => {
      cancelled = true;
      disposedRef.current = true;
    };
  }, [workshopId]);

  const persist = useCallback(
    (next: PersistedBoard) => {
      void set(storageKey(workshopId), next).catch(() => {
        // Best-effort: swallow quota / private-mode errors.
      });
    },
    [workshopId],
  );

  const addStroke = useCallback(
    (stroke: Stroke) => {
      setStrokes((prevStrokes) => {
        const nextStrokes = [...prevStrokes, stroke];
        setStickies((prevStickies) => {
          setActionLog((prevLog) => {
            const nextLog: BoardAction[] = [
              ...prevLog,
              { kind: "stroke", id: stroke.id },
            ];
            persist({
              strokes: nextStrokes,
              stickies: prevStickies,
              actionLog: nextLog,
            });
            return nextLog;
          });
          return prevStickies;
        });
        return nextStrokes;
      });
    },
    [persist],
  );

  const addSticky = useCallback(
    (sticky: Sticky) => {
      setStickies((prevStickies) => {
        const nextStickies = [...prevStickies, sticky];
        setStrokes((prevStrokes) => {
          setActionLog((prevLog) => {
            const nextLog: BoardAction[] = [
              ...prevLog,
              { kind: "sticky", id: sticky.id },
            ];
            persist({
              strokes: prevStrokes,
              stickies: nextStickies,
              actionLog: nextLog,
            });
            return nextLog;
          });
          return prevStrokes;
        });
        return nextStickies;
      });
    },
    [persist],
  );

  const updateSticky = useCallback(
    (id: string, patch: Partial<Pick<Sticky, "x" | "y" | "text">>) => {
      setStickies((prevStickies) => {
        let changed = false;
        const nextStickies = prevStickies.map((s) => {
          if (s.id !== id) return s;
          changed = true;
          return { ...s, ...patch };
        });
        if (!changed) return prevStickies;
        setStrokes((prevStrokes) => {
          setActionLog((prevLog) => {
            persist({
              strokes: prevStrokes,
              stickies: nextStickies,
              actionLog: prevLog,
            });
            return prevLog;
          });
          return prevStrokes;
        });
        return nextStickies;
      });
    },
    [persist],
  );

  const removeSticky = useCallback(
    (id: string) => {
      setStickies((prevStickies) => {
        if (!prevStickies.some((s) => s.id === id)) return prevStickies;
        const nextStickies = prevStickies.filter((s) => s.id !== id);
        setStrokes((prevStrokes) => {
          setActionLog((prevLog) => {
            const nextLog = prevLog.filter(
              (a) => !(a.kind === "sticky" && a.id === id),
            );
            persist({
              strokes: prevStrokes,
              stickies: nextStickies,
              actionLog: nextLog,
            });
            return nextLog;
          });
          return prevStrokes;
        });
        return nextStickies;
      });
    },
    [persist],
  );

  const clear = useCallback(() => {
    setStrokes(() => {
      setStickies(() => {
        setActionLog(() => {
          const empty: PersistedBoard = {
            strokes: [],
            stickies: [],
            actionLog: [],
          };
          persist(empty);
          return [];
        });
        return [];
      });
      return [];
    });
  }, [persist]);

  const undo = useCallback(() => {
    setActionLog((prevLog) => {
      if (prevLog.length === 0) return prevLog;
      const last = prevLog[prevLog.length - 1];
      if (!last) return prevLog;
      const nextLog = prevLog.slice(0, -1);

      if (last.kind === "stroke") {
        setStrokes((prevStrokes) => {
          const nextStrokes = prevStrokes.filter((s) => s.id !== last.id);
          setStickies((prevStickies) => {
            persist({
              strokes: nextStrokes,
              stickies: prevStickies,
              actionLog: nextLog,
            });
            return prevStickies;
          });
          return nextStrokes;
        });
      } else {
        setStickies((prevStickies) => {
          const nextStickies = prevStickies.filter((s) => s.id !== last.id);
          setStrokes((prevStrokes) => {
            persist({
              strokes: prevStrokes,
              stickies: nextStickies,
              actionLog: nextLog,
            });
            return prevStrokes;
          });
          return nextStickies;
        });
      }

      return nextLog;
    });
  }, [persist]);

  const replaceAll = useCallback(
    (next: PersistedBoard) => {
      const safeStrokes = Array.isArray(next.strokes) ? next.strokes : [];
      const safeStickies = Array.isArray(next.stickies) ? next.stickies : [];
      const safeLog = Array.isArray(next.actionLog)
        ? next.actionLog
        : [
            ...safeStrokes.map<BoardAction>((s) => ({
              kind: "stroke",
              id: s.id,
            })),
            ...safeStickies.map<BoardAction>((s) => ({
              kind: "sticky",
              id: s.id,
            })),
          ];
      setStrokes(safeStrokes);
      setStickies(safeStickies);
      setActionLog(safeLog);
      persist({
        strokes: safeStrokes,
        stickies: safeStickies,
        actionLog: safeLog,
      });
    },
    [persist],
  );

  return {
    strokes,
    stickies,
    actionLog,
    addStroke,
    addSticky,
    updateSticky,
    removeSticky,
    clear,
    undo,
    replaceAll,
    hydrated,
  };
}
